import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { FormControl, InputBase, FormControlLabel, Checkbox, Grid, MenuItem, Select } from '@material-ui/core';

import { injectIntl, FormattedMessage } from 'react-intl';

import { withRoomContext } from '../../RoomContext';
import * as settingsActions from '../../actions/settingsActions';
import * as meActions from '../../actions/meActions';
import * as requestActions from '../../actions/requestActions';

import { meProducersSelector } from '../Selectors';

import * as appPropTypes from '.././appPropTypes';

import PreviewVideo from './PreviewVideo';

import Logger from '../../Logger';

const logger = new Logger('VideoSettings');

const styles = (theme) => ({
	root : {
		height        : '100%',
		display       : 'flex',
		flexDirection : 'column',
		alignItems    : 'center',
		overflowY     : 'auto',
		padding       : theme.spacing(1)
	},
	setting : {
		padding : theme.spacing(2)
	},
	formControl :
	{
		display : 'flex'
	},
	videoContainer : {
		flex               : '0 0 auto',
		boxShadow          : 'var(--peer-shadow)',
		border             : 'var(--peer-border)',
		backgroundColor    : 'var(--peer-bg-color)',
		backgroundImage    : `url(${window.app_base_path}/svg/buddy.svg)`,
		backgroundPosition : 'bottom',
		backgroundSize     : 'auto 85%',
		backgroundRepeat   : 'no-repeat',
		zIndex             : 0
	}
});

const MediaSelect = withStyles((theme) => ({
	input: {
		borderRadius : 4,
		border       : '1px solid #ced4da',
		padding      : theme.spacing(1, 1.5),
		'&:focus': {
		borderColor: '#80bdff'
		}
	}
  }))(InputBase);


class VideoSettings extends React.PureComponent
{
	constructor(props)
	{
		super(props);

		this.state = {
			videoTrack  : null
		};
		this._localStream = null;
	}

	async componentDidMount()
	{
		const {
			joined,
			webcamDevices,
			selectedWebcam,
			webcamProducer
		} = this.props;

		let _videoInputDevices = [];

		// 设置界面上的设备默认选择
		let	_webcamDeviceId = selectedWebcam;

		_videoInputDevices = webcamDevices ? Object.values(webcamDevices).filter((v) => v.deviceId) : [];

		if(joined) {

			if (!_webcamDeviceId && _videoInputDevices.length > 0) {

				_webcamDeviceId = _videoInputDevices[0].deviceId;
			}
		} else {
			logger.debug(' componentDidMount() | calling enumerateDevices()');
			// 引入方法
			const { setWebcamDevices, setSelectedWebcamDevice } = this.props;

			try {

				if (_videoInputDevices.length === 0) {
					// 通过 api 加载设备列表,并且初始化状态变量
					const _webcamDevices = {};

					const devices = await navigator.mediaDevices.enumerateDevices();

					for (const device of devices) {
						if (device.kind === 'videoinput') {

							_videoInputDevices.push(device);
							_webcamDevices[device.deviceId] = device;

						}
					}
					setWebcamDevices(_webcamDevices);
				}

				if (!_webcamDeviceId && _videoInputDevices.length > 0) {
					_webcamDeviceId = _videoInputDevices[0].deviceId;
				}

				logger.debug(`当前选择的设备: \n ${_webcamDeviceId}`);

				setSelectedWebcamDevice(_webcamDeviceId);

			} catch (error) {
				logger.error(' componentDidMount() [error:"%o"]', error);
			}
		}

		await this.setVideoTrack(webcamProducer);
	}

	/**
	 * 获取 videoTrack
	 * @param {*} webcamProducer
	 */
	async setVideoTrack(webcamProducer) {

		const videoVisible = (
			Boolean(webcamProducer) &&
			!webcamProducer.locallyPaused &&
			!webcamProducer.remotelyPaused
		);

		logger.debug(`setVideoTrack -> %o`, webcamProducer);

		if (!videoVisible) {

			const { selectedWebcam, resolution, frameRate, notify, intl } = this.props;

			logger.debug(`通过 getUserMedia 获取视频流`);
			let track = null;

			try {

				if (this._localStream) {

					logger.debug(`setVideoTrack()  通过 track.stop() 清理历史视频流`)
					this._localStream.getVideoTracks().map((track) => track.stop());
					this._localStream = null;
				}

				this._localStream = await navigator.mediaDevices.getUserMedia({
					video: {
						deviceId: { ideal: selectedWebcam },
						...window.config.VIDEO_CONSTRAINS[resolution],
						frameRate
					}
				});
				[track] = this._localStream.getVideoTracks();

			} catch (error) {

				logger.error(' setVideoTrack() [error:"%o"]', error);

				notify('error', intl.formatMessage({
					id             : 'devices.cameraError',
					defaultMessage : 'An error occurred while accessing your camera'
				}));
			}

			this.setState({
				videoTrack: track
			})
		} else {

			logger.debug(`通过 webcamProducer 获取视频流`);

			this.setState({
				videoTrack : webcamProducer && webcamProducer.track
			})
		}
	}

	componentDidUpdate(prevProps)
	{
		if (prevProps.selectedWebcam !== this.props.selectedWebcam || prevProps.webcamProducer !== this.props.webcamProducer )
		{
			logger.debug(`componentDidUpdate -> setVideoTrack`);
			const { webcamProducer } = this.props;

			this.setVideoTrack(webcamProducer);
		}
	}

	componentWillUnmount()
	{
		if (this._localStream) {

			logger.debug(`通过 track.stop() 清理视频流`)

			this._localStream.getVideoTracks().map((track) => track.stop());

			this._localStream = null;
		}
	}


	render() {

		const {
			joined, toggleMirrorOwnVideo, setSelectedWebcamDevice,
			roomClient, selectedWebcam, mirrorOwnVideo, classes, webcamDevices, webcamInProgress
		} = this.props;

		const { videoTrack } = this.state;

		const videoVisible = (
			Boolean(videoTrack) &&
			videoTrack.enabled
		);

		const _videoInputDevices = webcamDevices ? Object.values(webcamDevices).filter((v) => v.deviceId) : [];

		const _webcamDeviceId = selectedWebcam ? selectedWebcam : '';

		return (
			<React.Fragment>
				<form className={classes.setting} autoComplete='off'>

					<Grid container spacing={2}>
						<Grid item xs={2}>
							<FormattedMessage
								id='settings.selectCamera'
								defaultMessage='Select video device'
							/>
						</Grid>
						<Grid item xs={10}>
							<FormControl className={classes.formControl}>
								<Select
									value={_webcamDeviceId}
									onChange={(event) =>
									{
										const _deviceId = event.target.value;
										if (_deviceId) {
											if (joined) {
												
												roomClient.updateWebcam({
													restart           : true,
													newDeviceId       : _deviceId,
													needSetVideoMuted : false
												});
											} else {

												this.setState({
													webcamDeviceId      : _deviceId
												}, () => {
													setSelectedWebcamDevice(_deviceId);
													logger.debug(`当前设备: ${_deviceId}`);
												});
											}
										}
									}}
									displayEmpty
									name={<FormattedMessage
										id             = 'settings.camera'
										defaultMessage = 'Camera'
									/>}
									autoWidth
									className={classes.selectEmpty}
									disabled={_videoInputDevices.length === 0 || webcamInProgress}
									input={<MediaSelect/>}
								>
									{ _videoInputDevices.map((video, index) =>
									{
										return (
											<MenuItem
												key={index}
												value={video.deviceId}
											>
												{video.label}
											</MenuItem>
										);
									})}
								</Select>
							</FormControl>
						</Grid>

						<Grid
							item xs={12}
							style={{ padding: '0px', width: '98%'}}
							className={
								classnames(
									classes.videoContainer
								)
							}
						>
							<PreviewVideo
								isMirrored={mirrorOwnVideo}
								videoTrack={videoTrack}
								videoVisible={videoVisible}
								videoContain
							>
							</PreviewVideo>
						</Grid>

						<Grid item xs={12}>
							<FormControlLabel control={<Checkbox
								checked={mirrorOwnVideo}
								onChange={(event) => {

									const _mirrorOwnVideo = event.target.value;

									if (_mirrorOwnVideo !== null) {
										toggleMirrorOwnVideo(_mirrorOwnVideo);
									}
								}} />}
								label={<FormattedMessage
								id='settings.mirrorVideo'
								defaultMessage='Mirror video(Flip left and right)'
							/>} />
						</Grid>
					</Grid>

				</form>
			</React.Fragment>
		);
	}
}

VideoSettings.propTypes =
{
	roomClient              : PropTypes.any.isRequired,

	mirrorOwnVideo          : PropTypes.bool.isRequired,
	selectedWebcam          : PropTypes.string.isRequired,
	resolution              : PropTypes.string.isRequired,
	frameRate               : PropTypes.number.isRequired,

	toggleMirrorOwnVideo    : PropTypes.func.isRequired,
	classes                 : PropTypes.object.isRequired,
	joined                  : PropTypes.bool.isRequired,

	webcamDevices           : PropTypes.object.isRequired,
	webcamInProgress        : PropTypes.bool.isRequired,

	webcamProducer          : appPropTypes.Producer,
	notify                  : PropTypes.func.isRequired

};

const mapStateToProps = (state) =>
{
	return {
		joined             : state.room.joined,
		webcamDevices      : state.me.webcamDevices,
		webcamInProgress   : state.me.webcamInProgress,
		...meProducersSelector(state),
		mirrorOwnVideo     : state.settings.mirrorOwnVideo,
		selectedWebcam     : state.settings.selectedWebcam,
		resolution         : state.settings.resolution,
		frameRate          : state.settings.frameRate
	};
};

const mapDispatchToProps = (dispatch) =>
{
	return {
		toggleMirrorOwnVideo: (flag) => {
			dispatch(settingsActions.toggleMirrorOwnVideo(flag));
		},
		setSelectedWebcamDevice: (deviceId) => {
			dispatch(settingsActions.setSelectedWebcamDevice(deviceId));
		},
		setWebcamDevices: (devices) => {
			dispatch(meActions.setWebcamDevices(devices))
		},
		notify: (type, msg) => {
			dispatch(requestActions.notify({
				type,
				text: msg
			}));
		}
	};
};

export default withRoomContext(connect(
	mapStateToProps,
	mapDispatchToProps,
	null,
	{
		areStatesEqual : (next, prev) =>
		{
			return (
				prev.room.joined === next.room.joined &&
				prev.me.webcamDevices === next.me.webcamDevices &&
				prev.me.webcamInProgress === next.me.webcamInProgress &&
				prev.producers === next.producers &&
				prev.settings.mirrorOwnVideo === next.settings.mirrorOwnVideo &&
				prev.settings.selectedWebcam === next.settings.selectedWebcam &&
				prev.settings.resolution === next.settings.resolution &&
				prev.settings.frameRate === next.settings.frameRate
			);
		}
	}
)(withStyles(styles)(injectIntl(VideoSettings))));